import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useMatch,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import type { ColumnVisibilityState } from "@tanstack/react-table";
import { Briefcase02, Building01, Circle, Plus } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import { ErrorState } from "@/components/Molecules/EmptyState/ErrorState";
import {
	JobsTableFilters,
	type JobsTableFiltersState,
} from "@/components/Molecules/FilterDrawer/JobsTableFilters";
import {
	BadgeOrEmptyCell,
	createAppColumnHelper,
	DateOrEmptyCell,
	FeaturedIconCell,
	LabelOrEmptyCell,
	PrimaryTextOrEmptyCell,
	SecondaryTextOrEmptyCell,
	TABLE_ICON_GUTTER_WIDTH_PX,
} from "@/components/Molecules/Table";
import {
	ActiveDrawerRow,
	useActiveDrawerRowId,
} from "@/components/Molecules/Table/active-drawer-row";
import { TableListEmptyState } from "@/components/Molecules/Table/TableListEmptyState";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { CreateJobModal } from "@/components/Organisms/CreateJobModal/CreateJobModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useCreatableSubOrganizations } from "@/hooks/use-creatable-sub-organizations";
import { useOrganizationHasSubOrganizations } from "@/hooks/use-organization-has-sub-organizations";
import { useSubOrganizationNameById } from "@/hooks/use-sub-organization-name-by-id";
import { useTableSettings } from "@/hooks/use-table-settings";
import { navigateDestinyDrawerEntity } from "@/lib/navigate-destiny-drawer-entity";
import { useDestinyDrawerRouteMatches } from "@/lib/use-destiny-drawer-route-matches";
import { authQueryOptions } from "@/services/auth";
import {
	type JobDisplay,
	type JobParams,
	jobQueryOptions,
	useExportJobs,
} from "@/services/job";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { resolveIconComponent } from "@/utils/icon-catalog";
import { JobTypeIcon } from "@/utils/jobs";
import { resolveStatusCategoryBadgeColor } from "@/utils/status-category-colors";
import { formatLongDate } from "@/utils/utils";

// ============================================================================
// TABLE SETTINGS CONSTANTS
// ============================================================================

const TABLE_KEY = "web.jobs";
/** Icon column pinned left of Number — not user-reorderable. */
const JOB_ICON_COLUMN_ID = "jobIcon";
const jobColumnHelper = createAppColumnHelper<JobDisplay>();

/** Primary visible columns — fixed product order. */
const VISIBLE_COLUMN_ORDER = [
	JOB_ICON_COLUMN_ID,
	"jobNumber",
	"name",
	"description",
	"statusName",
	"typeName",
	"subOrganizationName",
	"userAssignmentCount",
	"subContractorCount",
	"createdat",
	"updatedat",
	"id",
] as const;
const DEFAULT_COLUMN_ORDER = [...VISIBLE_COLUMN_ORDER];
const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityState = { id: false };
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

/**
 * Keeps Icon → Number → Name → Description → Status → Type → Sub-Organization →
 * Users → Subcontractors → Created → Updated → ID. Unknown/stale ids are dropped.
 */
const normalizeColumnOrder = (_columnOrder?: string[]) => {
	return [...DEFAULT_COLUMN_ORDER];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	status?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	status: [],
};

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/jobs",
)({
	component: Jobs,
	validateSearch: (search) => {
		return z
			.object({
				filterOpen: z.boolean().optional(),
				create: z.string().optional(),
			})
			.parse(search);
	},
});

function Jobs() {
	const navigate = useNavigate();
	const { matches, pathname } = useDestinyDrawerRouteMatches();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const workspaceMatch = useMatch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace",
		shouldThrow: false,
	});
	const activeJobId = useActiveDrawerRowId({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId",
		param: "jobId",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs",
	});
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (search.create === "job") {
			setIsOpen(true);
		}
	}, [search.create]);
	const [filters, setFilters] = useState<JobsTableFiltersState>({
		status: [],
		createdDateRange: null,
		lastUpdateDateRange: null,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["JobNumber"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	const columnVisibility = useMemo(
		() => ({
			...DEFAULT_COLUMN_VISIBILITY,
			...tableSettings.columnVisibility,
		}),
		[tableSettings.columnVisibility],
	);

	const params = useParams({ strict: false });

	const userInfoQuery = useQuery(authQueryOptions.userInfo());

	const defaultOrgId =
		userInfoQuery.data?.organizationUserInfo?.[0]?.organization?.id ??
		"unknown";

	const currentOrgId =
		(params as { orgId?: string }).orgId ?? defaultOrgId ?? "unknown";

	const { hasSubOrganizations } =
		useOrganizationHasSubOrganizations(currentOrgId);

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev: JobsTableFiltersState) => ({
				...prev,
				status: tableSettings.filters.status ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		jobNumber: ["JobNumber"],
		name: ["Name"],
		description: ["Description"],
		statusName: ["Status.Name"],
		typeName: ["Type.Name"],
		userAssignmentCount: ["UserAssignmentCount"],
		subOrganizationName: ["SubOrganization.Name"],
		subContractorCount: ["SubContractorCount"],
		createdat: ["CreatedAt"],
		updatedat: ["UpdatedAt"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportJobs();
	const debouncedFilters = useDebounced(filters, 500);

	const filterConfig: FilterConfig = {
		status: { paramName: "JobStatusIds", transform: "join" },
		type: { paramName: "JobTypeIds", transform: "join" },
		"createdDateRange.start": {
			paramName: "CreatedAfter",
			transform: "dateStart",
		},
		"createdDateRange.end": {
			paramName: "CreatedBefore",
			transform: "dateEnd",
			dateStartField: "createdDateRange.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "UpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "UpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
	};

	const filterParams = useMemo(() => {
		return buildFilterParams(debouncedFilters, filterConfig);
	}, [debouncedFilters]);

	const jobCount = useQuery(
		jobQueryOptions.count({
			SearchTerm: searchTerm,
			OrganizationId: currentOrgId,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredJobCount = useQuery(
		jobQueryOptions.count({
			OrganizationId: currentOrgId,
		}),
	);

	const allJobs = useQuery(
		jobQueryOptions.list(startIndex, pageSize, {
			SearchTerm: searchTerm,
			OrganizationId: currentOrgId,
			...filterParams,
			...sortParams,
		}),
	);

	const canCreateJobs = useCreatableSubOrganizations({
		actionName: "CreateJob",
		organizationId: currentOrgId,
		entityType: "Job",
	});

	const subOrganizationNameById = useSubOrganizationNameById(
		currentOrgId,
		!!currentOrgId && currentOrgId !== "unknown" && hasSubOrganizations,
	);

	const data = useMemo(() => {
		return allJobs.data ?? [];
	}, [allJobs.data]);

	const pageCount = useMemo(() => {
		if (!jobCount.data) return 0;
		return Math.ceil(jobCount.data / pageSize);
	}, [jobCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allJobs.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = jobCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					SearchTerm: searchTerm,
					OrganizationId: currentOrgId,
					...filterParams,
					...sortParams,
					columns: visibleColumns?.join(","),
				} as JobParams & { columns?: string },
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("jobs"));
				},
			},
		);
	};

	const handleRowClick = (job: JobDisplay) => {
		if (!job.id) return;
		navigateDestinyDrawerEntity({
			navigate,
			matches,
			pathname,
			entityParam: "jobId",
			entityId: job.id,
			closedHref: `/web/${currentOrgId}/jobs/${job.id}/details`,
		});
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: JobsTableFiltersState) => {
		setFilters(newFilters);
		// Save filters for persistence (only serializable fields)
		saveFilters({
			status: newFilters.status,
		});
	};

	const jobColumns = useMemo(
		() =>
			jobColumnHelper.columns([
				jobColumnHelper.display({
					id: JOB_ICON_COLUMN_ID,
					header: "",
					enableSorting: false,
					enableHiding: false,
					size: TABLE_ICON_GUTTER_WIDTH_PX,
					minSize: TABLE_ICON_GUTTER_WIDTH_PX,
					maxSize: TABLE_ICON_GUTTER_WIDTH_PX,
					meta: {
						isIconGutter: true,
						disableColumnReorder: true,
						rowFullViewAction: (row: unknown) => {
							const job = row as JobDisplay;
							if (!job.id) return;
							navigate({
								to: "/web/$orgId/jobs/$jobId/workspace",
								params: { orgId: currentOrgId, jobId: job.id },
							});
						},
					},
					cell: () => <FeaturedIconCell icon={Briefcase02} />,
				}),
				jobColumnHelper.accessor(
					(row) => (row.jobNumber != null ? `# ${row.jobNumber}` : null),
					{
						id: "jobNumber",
						header: t("main.jobs.table.columns.number"),
						enableSorting: true,
						minSize: 100,
						cell: () => <SecondaryTextOrEmptyCell />,
					},
				),
				jobColumnHelper.accessor("name", {
					id: "name",
					header: t("main.jobs.table.columns.name"),
					enableSorting: true,
					meta: {
						headerProps: {
							isRowHeader: true,
						},
					},
					minSize: 300,
					cell: () => <PrimaryTextOrEmptyCell />,
				}),
				jobColumnHelper.accessor("description", {
					id: "description",
					header: t("main.jobs.table.columns.description"),
					enableSorting: true,
					minSize: 250,
					cell: () => <SecondaryTextOrEmptyCell />,
				}),
				jobColumnHelper.accessor("statusName", {
					id: "statusName",
					header: t("main.jobs.table.columns.status"),
					enableSorting: true,
					minSize: 100,
					cell: ({ row }) => (
						<BadgeOrEmptyCell
							type="pill-color"
							color={resolveStatusCategoryBadgeColor(
								row.original.statusCategoryIconColor,
							)}
							icon={
								resolveIconComponent(row.original.statusCategoryIcon ?? "") ??
								Circle
							}
						/>
					),
				}),
				jobColumnHelper.accessor("typeName", {
					id: "typeName",
					header: t("main.jobs.table.columns.type"),
					enableSorting: true,
					minSize: 150,
					cell: ({ row }) => {
						const typeName = row.original.typeName ?? "";
						const Icon =
							resolveIconComponent(row.original.typeIcon ?? "") ??
							JobTypeIcon(typeName);
						return <LabelOrEmptyCell icon={Icon} />;
					},
				}),
				jobColumnHelper.accessor("subOrganizationId", {
					id: "subOrganizationName",
					header: t("main.jobs.table.columns.subOrganization"),
					enableSorting: true,
					minSize: 200,
					cell: ({ row }) => {
						const subOrganizationId = row.original.subOrganizationId;
						const name = subOrganizationId
							? subOrganizationNameById.get(subOrganizationId)
							: undefined;
						return (
							<LabelOrEmptyCell icon={Building01} primary={name ?? null} />
						);
					},
				}),
				jobColumnHelper.accessor(
					(row) =>
						row.userAssignmentCount != null && row.userAssignmentCount >= 0
							? String(row.userAssignmentCount)
							: null,
					{
						id: "userAssignmentCount",
						header: t("main.jobs.table.columns.users"),
						enableSorting: true,
						minSize: 100,
						cell: () => <SecondaryTextOrEmptyCell />,
					},
				),
				jobColumnHelper.accessor(
					(row) =>
						row.subContractorCount != null && row.subContractorCount >= 0
							? String(row.subContractorCount)
							: null,
					{
						id: "subContractorCount",
						header: t("main.jobs.table.columns.subContractors"),
						enableSorting: true,
						minSize: 150,
						cell: () => <SecondaryTextOrEmptyCell />,
					},
				),
				jobColumnHelper.accessor(
					(row) => (row.createdAt ? formatLongDate(row.createdAt) : null),
					{
						id: "createdat",
						header: t("main.jobs.table.columns.created"),
						enableSorting: true,
						minSize: 175,
						cell: () => <DateOrEmptyCell />,
					},
				),
				jobColumnHelper.accessor(
					(row) => (row.updatedAt ? formatLongDate(row.updatedAt) : null),
					{
						id: "updatedat",
						header: t("main.jobs.table.columns.lastUpdate"),
						enableSorting: true,
						minSize: 175,
						cell: () => <DateOrEmptyCell />,
					},
				),
				jobColumnHelper.accessor("id", {
					id: "id",
					header: t("main.jobs.drawer.tabs.details.id"),
					enableSorting: false,
					minSize: 200,
					cell: ({ row }) =>
						row.original.id ? (
							<CopyableId value={row.original.id} />
						) : (
							<EmptyValue />
						),
				}),
			]),
		[currentOrgId, navigate, subOrganizationNameById, t],
	);

	const columns = hasSubOrganizations
		? jobColumns
		: jobColumns.filter((column) => column.id !== "subOrganizationName");

	const openCreateJob = () => {
		setIsOpen(true);
		navigate({
			to: "/web/$orgId/jobs",
			params: { orgId: currentOrgId },
			search: { create: "job" },
		});
	};

	const hasActiveSearchOrFilters =
		searchTerm.trim().length > 0 || Object.keys(filterParams).length > 0;

	const jobsEmptyState = allJobs.isError ? (
		<ErrorState
			title={t("main.jobs.table.errors.title")}
			description={t("main.jobs.table.errors.description")}
			actionLabel={t("common.actions.tryAgain")}
			onAction={handleRefresh}
		/>
	) : hasActiveSearchOrFilters ? (
		<TableSearchEmptyState titleKey="main.jobs.table.empty.noSearchResults" />
	) : (
		<TableListEmptyState
			title={t("main.jobs.table.empty.title")}
			description={t("main.jobs.table.empty.description")}
			actionLabel={canCreateJobs.canCreate ? t("common.actions.add") : undefined}
			onAction={canCreateJobs.canCreate ? openCreateJob : undefined}
		/>
	);

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					jobQueryOptions.list(nextStartIndex, pageSize, {
						SearchTerm: searchTerm,
						OrganizationId: currentOrgId,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}

		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					jobQueryOptions.list(prevStartIndex, pageSize, {
						SearchTerm: searchTerm,
						OrganizationId: currentOrgId,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		currentOrgId,
		searchTerm,
		filterParams,
		sortParams,
		queryClient,
	]);

	// Workspace is a full second-level page — skip landing table chrome.
	if (workspaceMatch) {
		return <Outlet />;
	}

	return (
		<ActiveDrawerRow.Root rowId={activeJobId}>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("main.jobs.table.title")}
						description={t("main.jobs.table.description")}
						actions={
							canCreateJobs.canCreate
								? [
										{
											label: t("common.actions.add"),
											icon: Plus,
											color: "accent",
											onPress: openCreateJob,
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<ActiveDrawerRow.Table
					tableKey={TABLE_KEY}
					data={allJobs.isError ? [] : data}
					columns={columns}
					showHeaderPadding={false}
					zebraStriping
					isLoading={allJobs.isLoading}
					isFetching={allJobs.isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					emptyState={jobsEmptyState}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={jobCount.data}
					totalRowCount={unFilteredJobCount.data}
					sortFieldMapping={sortFieldMapping}
					defaultSortField="jobNumber"
					onSortChange={({ fields, descending }) => {
						setSortParams({
							"OrderBy.Fields": fields,
							"OrderBy.Descending": descending,
						});
					}}
					filterLayout="bar"
					filterBarType="landing"
					filterPlaceholder={t("main.jobs.table.search.placeholder")}
					filterInputTestId="jobs-table-search-input"
					filterValue={searchTerm}
					onDebouncedFilterChange={handleDebouncedSearchChange}
					filtersTrailingContent={
						<JobsTableFilters value={filters} onChange={handleFilterChange} />
					}
					enableTableActions
					hasActiveFilters={Object.keys(filterParams).length > 0}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					tableOptionsPortalTarget={tableOptionsHost}
					enableColumnReordering
					onRowClick={handleRowClick}
					getRowId={(row) => row.id ?? ""}
					enableColumnResizing
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={columnVisibility}
					defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
					settingsLoaded={settingsLoaded}
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
					// PageLayout already gaps header → body; drop FilterBar top pad so it isn't doubled.
					filterBarClassName="pt-0"
				/>
			</PageLayout>

			<CreateJobModal
				isOpen={isOpen}
				setIsOpen={(o) => {
					setIsOpen(o);
					if (!o)
						navigate({
							to: "/web/$orgId/jobs",
							params: { orgId: currentOrgId },
						});
				}}
				handleClose={() => {
					setIsOpen(false);
					navigate({
						to: "/web/$orgId/jobs",
						params: { orgId: currentOrgId },
					});
				}}
				pageSize={pageSize}
				pageIndex={pageIndex}
			/>
			<Outlet />
		</ActiveDrawerRow.Root>
	);
}
